import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Search } from "lucide-react";
import { clsx } from "clsx";
import { Input, Pill, usePopover } from "../ui";
import type { DocHandle } from "../sync/docManager";
import { useDocSelector } from "../core/useDocSelector";
import { listTasks } from "../core/tasks";
import { TaskSearch, type SearchHit } from "./index";
import { applyQuickAdd, quickAddPropDefs } from "../core/ops";
import { parseQuickAdd } from "../core/quickadd";
import { listDashboards } from "../dashboards/model";
import { readPomodoro, startPomodoro, stopPomodoro } from "../tracking/pomodoro";
import { stopTracking } from "../tracking/timelog";

interface Command {
  id: string;
  label: string;
  run(): void;
}

/**
 * App-bar search: `/` or Ctrl+K focuses it; Enter opens the first hit. `collapsible` (narrow and medium layouts):
 * an icon until used, then the field spans the bar; it folds back when it loses focus empty.
 */
export function SearchBox({ core, collapsible = false }: { core: DocHandle | null; collapsible?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const { open, setOpen, ref } = usePopover<HTMLDivElement>();
  const [expanded, setExpanded] = useState(false);
  const folded = collapsible && !expanded;
  const focusField = () => {
    setExpanded(true);
    // the field exists after the next render when it was folded
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const tasks = useDocSelector(core, (doc) => listTasks(doc));
  const engine = useMemo(() => new TaskSearch(), []);
  useEffect(() => {
    engine.rebuild(tasks ?? []);
  }, [engine, tasks]);
  const hits: SearchHit[] = useMemo(() => engine.search(q.replace(/^>/, "")), [engine, q]);
  const dashboards = useDocSelector(core, (doc) =>
    listDashboards(doc).map((d) => ({ slug: d.slug, title: d.title })),
  );

  // command palette (spec §10, Ctrl+K): navigation and actions, filtered by the same query
  const commands: Command[] = useMemo(() => {
    const go = (path: string) => () => navigate(path);
    const all: Command[] = [
      { id: "list", label: t("nav.list"), run: go("/list") },
      { id: "planner", label: t("nav.planner"), run: go("/planner") },
      { id: "worklog", label: t("nav.worklog"), run: go("/worklog") },
      { id: "settings", label: t("nav.settings"), run: go("/settings") },
      ...(dashboards ?? []).map((d) => ({
        id: `d:${d.slug}`,
        label: `${t("dashboards.menu")}: ${d.title}`,
        run: go(`/d/${d.slug}`),
      })),
    ];
    if (core && !core.readOnly) {
      all.push({
        id: "pomodoro",
        label: readPomodoro(core.doc) ? t("pomodoro.stop") : t("pomodoro.start"),
        run: () => (readPomodoro(core.doc) ? stopPomodoro(core.doc) : startPomodoro(core.doc)),
      });
      all.push({ id: "stop", label: t("tracking.stop"), run: () => void stopTracking(core.doc) });
    }
    const n = q.replace(/^>/, "").trim().toLowerCase();
    if (!n) return q.startsWith(">") ? all : [];
    const matched = all.filter((c) => c.label.toLowerCase().includes(n));
    if (core && !core.readOnly && !q.startsWith(">")) {
      matched.push({
        id: "create",
        label: t("palette.create", { text: q.trim() }),
        run: () =>
          void applyQuickAdd(
            core.doc,
            parseQuickAdd(q.trim(), { propDefs: quickAddPropDefs(core.doc), now: new Date() }),
          ),
      });
    }
    return matched;
  }, [q, t, navigate, dashboards, core]);
  const total = hits.length + commands.length;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        focusField();
      } else if (e.key === "/" && !typing) {
        e.preventDefault();
        focusField();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const runCommand = (c: Command) => {
    c.run();
    setQ("");
    setOpen(false);
  };
  const pick = (h: SearchHit) => {
    navigate(`/t/${h.id}`);
    setQ("");
    setOpen(false);
  };

  if (folded)
    return (
      <button
        type="button"
        className="grid h-[30px] w-[30px] place-items-center rounded-sm border border-neutral-200 bg-[var(--surface)] text-neutral-600 hover:border-neutral-700 touch:h-11 touch:w-11 dark:border-neutral-800 dark:text-neutral-300"
        aria-label={t("search.label")}
        title={t("search.label")}
        onClick={focusField}
        data-testid="search-open"
      >
        <Search size={15} aria-hidden />
      </button>
    );

  return (
    <div
      ref={ref}
      className={clsx(
        "relative",
        // unfolded on a small screen: over the whole bar, so the field has room
        collapsible && "absolute inset-x-2 top-1/2 z-40 -translate-y-1/2 rounded-md bg-[var(--ground)]",
      )}
    >
      <div className="relative">
        <Search
          size={14}
          aria-hidden
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400"
        />
        <Input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={open && hits.length > 0}
          aria-controls="search-results"
          aria-label={t("search.label")}
          placeholder={t("search.placeholder")}
          className={clsx("h-[30px] py-0 pl-8 text-[13px] touch:h-11", collapsible ? "w-full" : "w-56")}
          onBlur={() => {
            if (collapsible && !q) window.setTimeout(() => setExpanded(false), 150);
          }}
          value={q}
          data-testid="search"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setCursor(0);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setCursor((c) => Math.min(c + 1, total - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setCursor((c) => Math.max(c - 1, 0));
            } else if (e.key === "Enter" && cursor < total) {
              e.preventDefault();
              if (hits[cursor]) pick(hits[cursor]);
              else runCommand(commands[cursor - hits.length]);
            } else if (e.key === "Escape") {
              setQ("");
              inputRef.current?.blur();
            }
          }}
        />
      </div>
      {open && (q.trim() || q.startsWith(">")) && total + 1 > 0 && (
        <ul
          id="search-results"
          role="listbox"
          data-testid="search-results"
          className={clsx(
            "absolute top-9 z-40 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700",
            collapsible ? "inset-x-0" : "right-0 w-96",
          )}
        >
          {total === 0 && <li className="px-2 py-1.5 text-neutral-500">{t("search.none")}</li>}
          {hits.map((h, i) => (
            <li key={h.id}>
              <button
                type="button"
                role="option"
                aria-selected={i === cursor}
                onMouseEnter={() => setCursor(i)}
                onClick={() => pick(h)}
                className={clsx(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left",
                  i === cursor && "bg-neutral-100 dark:bg-neutral-800",
                )}
              >
                <span className={clsx("min-w-0 flex-1 truncate", h.done && "text-neutral-500 line-through")}>
                  {h.title || t("task.untitled")}
                </span>
                <Pill tone="neutral">{t(`types.${h.typeId}`, { defaultValue: h.typeId })}</Pill>
              </button>
            </li>
          ))}
          {commands.map((c, j) => {
            const i = hits.length + j;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === cursor}
                  data-testid="palette-command"
                  data-command={c.id}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => runCommand(c)}
                  className={clsx(
                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-neutral-600 dark:text-neutral-300",
                    i === cursor && "bg-neutral-100 dark:bg-neutral-800",
                  )}
                >
                  <span className="font-mono text-[11px] text-neutral-400">›</span>
                  <span className="min-w-0 flex-1 truncate">{c.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
